<script setup>
import {computed} from "vue";

const props = defineProps({
    bulk_send: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['export'])

const statusLabel = computed(() => props.bulk_send.status.replace(/_/g, ' '))

const sender = computed(() => {
    const user = props.bulk_send.created_by

    return user ? `${user.first_name} ${user.last_name}` : 'Unknown'
})

const startedAt = computed(() => new Date(props.bulk_send.created_at).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}))
</script>

<template>
    <router-link :to="`/chats/bulk-sends/${bulk_send.uuid}`" class="bulk-send-row-item">
        <div class="bulk-send-row-item-template">
            <p class="bulk-send-row-item-name">{{ bulk_send.auto_reply?.description || bulk_send.auto_reply?.name }}</p>
            <p class="bulk-send-row-item-meta">{{ startedAt }} · {{ sender }}</p>
        </div>

        <div class="bulk-send-row-item-counts">
            <span class="--total">{{ bulk_send.recipient_count }} recipients</span>
            <span class="--sent">{{ bulk_send.sent_count }} sent</span>
            <span class="--failed" v-if="bulk_send.failed_count">{{ bulk_send.failed_count }} failed</span>
            <span class="--skipped" v-if="bulk_send.skipped_count">{{ bulk_send.skipped_count }} skipped</span>
        </div>

        <div class="bulk-send-row-item-actions">
            <div :class="['bulk-send-row-item-status', `--${bulk_send.status}`]">{{ statusLabel }}</div>

            <brand-button @click.prevent.stop="emit('export', bulk_send)" as="button" variant="small" title="Export to CSV">
                <ah-icon name="arrow-in"/>
            </brand-button>
        </div>
    </router-link>
</template>

<style scoped lang="scss">
.bulk-send-row-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    padding: 2rem;
    background: var(--color-white);
    border-radius: 1.6rem;
    text-decoration: none;

    .bulk-send-row-item-name {
        @include font(1.6, 2.2, black);
    }

    .bulk-send-row-item-meta {
        @include font(1.2, 1.8, book);
        color: var(--color-grey-hazy);
    }

    .bulk-send-row-item-counts {
        display: flex;
        gap: 1.5rem;
        @include font(1.2, 1.8, book);

        .--sent { color: var(--color-tiffany-dark); }
        .--failed { color: var(--color-secondary-orange); }
        .--skipped { color: var(--color-grey-hazy); }
    }

    .bulk-send-row-item-actions {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .bulk-send-row-item-status {
        @include font(1.2, 1.2, black);
        padding: .8rem 1.6rem;
        border-radius: 8rem;
        background: var(--color-dew);
        text-transform: capitalize;

        &.--sending { background: var(--color-dew); }
        &.--failed { background: var(--color-secondary-orange); color: var(--color-white); }
        &.--completed { background: var(--color-tiffany-dark); color: var(--color-white); }
    }

    @include dark() {
        background: #192D39;

        .bulk-send-row-item-name { color: var(--color-white); }
    }

    @include mobile() {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }
}
</style>
